import {
  FormControl,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Typography
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import {
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowEmpty,
  globalDateTimeFormat,
  RelativeTime
} from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import usePrevious from "~/hooks/usePrevProps";

import { extractUserNameFromAuth0Id } from "~/lib/helpers";
import { selectSiteFulfillmentCenterId } from "~/redux/selectors/siteSelectors";
import { useGetAllFulfillmentCentersQuery } from "~/redux/warehouse/fulfillmentCenter.hooks";
import { useGetInternalTestEventFeedQuery } from "~/redux/warehouse/testingTools.hook";

const TestingToolsLogs = () => {
  const { t } = useTranslation();
  const { data: fulfillmentCenters } = useGetAllFulfillmentCentersQuery();

  const siteFulfillmentCenterId = useAppSelector(selectSiteFulfillmentCenterId);
  const [currentPage, setPage] = useState<number>(1);
  const [selectedFulfillmentCenter, setSelectedFulfillmentCenter] = useState<
    string | null
  >(null);
  const prevSelectedFulfillmentCenter = usePrevious<string | null>(
    selectedFulfillmentCenter
  );
  const limit = 10;
  const {
    data: internalTestEvents = [],
    isLoading,
    isFetching
  } = useGetInternalTestEventFeedQuery(
    selectedFulfillmentCenter
      ? { selectedFulfillmentCenterId: selectedFulfillmentCenter }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...queryResult }) => ({
        ...queryResult,
        data: data
          ? data.toSorted((a, b) =>
              dayjs(a.eventTimestamp).isBefore(dayjs(b.eventTimestamp)) ? 1 : -1
            )
          : []
      })
    }
  );
  const isGettingTestingLogs = isLoading || isFetching;

  useEffect(() => {
    if (
      currentPage > 1 &&
      selectedFulfillmentCenter !== prevSelectedFulfillmentCenter &&
      prevSelectedFulfillmentCenter !== null
    ) {
      setPage(1);
    }
  }, [currentPage, prevSelectedFulfillmentCenter, selectedFulfillmentCenter]);

  useEffect(() => {
    if (
      siteFulfillmentCenterId &&
      fulfillmentCenters?.length &&
      !selectedFulfillmentCenter
    ) {
      setSelectedFulfillmentCenter(siteFulfillmentCenterId);
    }
    // This effect only runs when siteFulfillmentCenterId or fulfillmentCenters change
    // it’s what initially sets selectedFulfillmentCenter, and we omit selectedFulfillmentCenter
    // from the deps to avoid resetting it and causing an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFulfillmentCenterId, fulfillmentCenters]);

  const testingLogsDataToDisplay = useMemo(() => {
    if (!selectedFulfillmentCenter?.length) return [];
    return internalTestEvents.slice(
      (currentPage - 1) * limit,
      limit * currentPage
    );
  }, [internalTestEvents, currentPage, selectedFulfillmentCenter]);

  return (
    <Paper
      sx={{
        pt: 2,
        px: 3,
        mt: 1
      }}
      aria-label="testing logs"
      role="region"
    >
      <Stack flexDirection="column" gap={3}>
        <Box>
          <Typography fontWeight={500} variant="h6">
            {t("testing logs")}
          </Typography>
          <Typography variant="subtitle1" fontWeight="normal">
            {t("testing logs description")}
          </Typography>
        </Box>
        <FormControl
          sx={{
            maxWidth: "400px",
            mt: 1
          }}
        >
          <FormLabel id="fc-select">{t("fulfillment center")}</FormLabel>
          <Select
            labelId="fc-select"
            value={selectedFulfillmentCenter ?? ""}
            variant="outlined"
            size="medium"
            displayEmpty
            onChange={(e: SelectChangeEvent) =>
              setSelectedFulfillmentCenter(e.target.value)
            }
          >
            <MenuItem value="">
              <em>{t("none")}</em>
            </MenuItem>
            {fulfillmentCenters?.map((fc) => (
              <MenuItem
                key={fc.fulfillmentCenterId}
                value={fc.fulfillmentCenterId}
              >
                {fc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <ASTableV2
          gridTemplateColumns="repeat(5, auto)"
          gridTemplateRows="auto"
          isLoading={isGettingTestingLogs}
          pagination={{
            setPage: (pageNumber: number) => setPage(pageNumber),
            pageCount: Math.ceil(internalTestEvents.length / limit),
            page: currentPage
          }}
        >
          <ASTableV2Header aria-label="testing logs header">
            <ASTableV2Row>
              <ASTableV2Cell id="date-time">
                <Typography variant="tableBody">
                  {t("date and time")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell id="test-type">
                <Typography variant="tableBody">{t("test type")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell id="device-id">
                <Typography variant="tableBody">{t("device id")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell id="username">
                <Typography variant="tableBody">{t("user")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell id="fulfillment-center-name">
                <Typography variant="tableBody">
                  {t("fulfillment center")}
                </Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>
          {!isGettingTestingLogs && (
            <ASTableV2Body>
              {!testingLogsDataToDisplay.length && (
                <ASTableV2RowEmpty>{"No testing logs found"}</ASTableV2RowEmpty>
              )}
              {testingLogsDataToDisplay.map((log) => {
                const {
                  data,
                  eventTimestamp,
                  eventType,
                  userId,
                  eventId,
                  fulfillmentCenterId
                } = log;
                const fulfilmentCenterNameToDisplay = fulfillmentCenters?.find(
                  (fc) => fc.fulfillmentCenterId === fulfillmentCenterId
                )?.name;

                return (
                  <ASTableV2Row key={eventId} aria-label="testing log">
                    <ASTableV2Cell
                      aria-describedby="date-time"
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start"
                      }}
                    >
                      <RelativeTime time={eventTimestamp} variant="tableBody" />
                      <Typography variant="subtitle1">
                        {dayjs(eventTimestamp).format(globalDateTimeFormat)}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell aria-describedby="test-type">
                      <Typography variant="tableBody">{eventType}</Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell aria-describedby="device-id">
                      <Typography variant="tableBody">
                        {data.DeviceId || "-"}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell aria-describedby="username">
                      <Typography variant="tableBody">
                        {userId ? extractUserNameFromAuth0Id(userId) : "-"}
                      </Typography>
                    </ASTableV2Cell>
                    <ASTableV2Cell aria-describedby="fulfillment-center-name">
                      <Typography variant="tableBody">
                        {fulfilmentCenterNameToDisplay ?? "-"}
                      </Typography>
                    </ASTableV2Cell>
                  </ASTableV2Row>
                );
              })}
            </ASTableV2Body>
          )}
        </ASTableV2>
      </Stack>
    </Paper>
  );
};

export default TestingToolsLogs;
